/**
 * @file ButtonLiveSample.tsx
 * @description Живой образец кнопки под настройками (phase19.16, партия
 * SB120, этап 3а; uiux-sb120-stage3 §3 «Кнопка» — `[ КУПИТЬ → ]`): вид,
 * заливка, цвет текста, рамка, высота и скругление темы — как на сайте.
 * «Авто» — как витрина (`buttonAutoTextColor`): у кнопки с заливкой —
 * `pickButtonText` по заливке, у контурной и ссылки — цвет ссылки (акцент;
 * фон секции образец не знает).
 * Образец — подсказка, не канва: шрифт и наведение показывает канва.
 */

import { buttonRadiusCss, colorRefHex, type SectionElementOf, type SiteTheme } from "@chaiberi/shared";

import { buttonAutoTextColor } from "../../../../lib/site-builder/typo-kind-meta";

type Button = SectionElementOf<"button">;

/** Цвета образца: фон, текст, рамка. */
function sampleColors(element: Button, theme: SiteTheme): { background: string; color: string; border: string } {
  const colors = theme.colors;
  const primary = element.variant === "primary";
  const background = primary ? (element.fill ? colorRefHex(element.fill, colors) : colors.button) : "transparent";
  const auto = buttonAutoTextColor(element, undefined, colors) ?? colors.accent;
  const own = element.typo?.main?.color;
  const color = own ? (own.kind === "role" ? colors[own.role] : own.color) : auto;
  const width = element.variant === "link" ? 0 : (element.border?.width ?? (element.variant === "outline" ? 1 : 0));
  const borderColor = element.border?.color ? colorRefHex(element.border.color, colors) : "currentColor";
  return { background, color, border: width > 0 ? `${width}px solid ${borderColor}` : "none" };
}

export function ButtonLiveSample({ element, theme }: { element: Button; theme: SiteTheme | null }) {
  if (!theme) return null;
  const { background, color, border } = sampleColors(element, theme);
  const link = element.variant === "link";
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="type-caption text-on-surface-variant">Образец</p>
      <div className="flex min-w-0 items-center rounded-lg bg-surface-low p-3">
        <span
          aria-hidden
          className="inline-flex max-w-full items-center justify-center truncate text-sm font-semibold"
          style={{
            minHeight: element.height ?? 48,
            padding: link ? 0 : "0 24px",
            background,
            color,
            border,
            borderRadius: buttonRadiusCss(theme.shapes.buttonRadius),
            textDecoration: link ? "underline" : undefined,
          }}
        >
          {element.label || "Кнопка"}
        </span>
      </div>
    </div>
  );
}
